import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { ChatsCircle, CircleNotch, CheckCircle, PaperPlaneTilt } from "@phosphor-icons/react";

export function IntakeInterview({ checklists }) {
  const qc = useQueryClient();
  const [engId, setEngId] = useState(null);
  const [answer, setAnswer] = useState("");
  const [pendingMsg, setPendingMsg] = useState(null);
  const scrollRef = useRef();
  const active = engId || checklists?.[0]?.engagement_id;

  const { data: session, refetch } = useQuery({
    queryKey: ["intake", active],
    queryFn: () => api.get(`/portal/intake/${active}`).then((r) => r.data),
    enabled: !!active,
  });

  const start = useMutation({
    mutationFn: () => api.post("/portal/intake/start", { engagement_id: active }),
    onSuccess: () => refetch(),
    onError: (e) => toast.error(errText(e)),
  });
  const reply = useMutation({
    mutationFn: (text) => api.post("/portal/intake/reply", { session_id: session.id, answer: text }),
    onSuccess: ({ data }) => {
      setPendingMsg(null);
      refetch();
      if (data.added_documents?.length) {
        toast.success(`Added to your checklist: ${data.added_documents.map((d) => d.label).join(", ")}`);
        qc.invalidateQueries({ queryKey: ["portal"] });
        qc.invalidateQueries({ queryKey: ["portal-extras"] });
      }
      if (data.done) toast.success("Interview complete — your document list is now personalized!");
    },
    onError: (e) => { setPendingMsg(null); toast.error(errText(e)); },
  });

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }); }, [session, pendingMsg]);

  if (!checklists?.length) return null;
  const started = !!session?.id;
  const done = session?.status === "completed";
  const send = () => {
    const t = answer.trim();
    if (!t) return;
    setAnswer("");
    setPendingMsg(t);
    reply.mutate(t);
  };

  // Audit-readiness score: 20% for starting, 60% when actively answering, 100% when done.
  const answered = (session?.messages || []).filter((m) => m.role === "client").length;
  const readiness = done ? 100 : (started ? Math.min(90, 20 + answered * 15) : 0);
  const readinessColor = readiness >= 80 ? "text-emerald-700 bg-emerald-50 border-emerald-200" : readiness >= 40 ? "text-amber-700 bg-amber-50 border-amber-200" : "text-slate-600 bg-slate-50 border-slate-200";

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5 relative hover:shadow-md transition-shadow duration-200" data-testid="intake-interview">
      {started && (
        <div className={`absolute -top-2.5 right-4 border rounded-full px-2 py-0.5 text-[9px] uppercase tracking-wider font-semibold shadow-sm ${readinessColor}`} data-testid="intake-audit-readiness-badge">
          Audit readiness · {readiness}%
        </div>
      )}
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-medium text-slate-900 flex items-center gap-1.5"><ChatsCircle size={15} /> Smart organizer interview</h3>
        {done && <span className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm px-2 py-0.5 flex items-center gap-1"><CheckCircle size={11} weight="fill" /> COMPLETE</span>}
      </div>
      <p className="text-[11px] text-zinc-500 mb-3">Skip the 40-page PDF organizer — answer a few quick questions and we'll build your exact document list.</p>
      {checklists.length > 1 && (
        <select data-testid="intake-engagement-select" className="mb-3 w-full text-xs border border-zinc-200 rounded-md px-2 py-1.5 bg-white" value={active} onChange={(e) => setEngId(e.target.value)}>
          {checklists.map((c) => <option key={c.engagement_id} value={c.engagement_id}>{c.service_type} · {c.tax_year}</option>)}
        </select>
      )}
      {!started ? (
        <Button data-testid="intake-start-btn" size="sm" className="w-full" disabled={start.isPending} onClick={() => start.mutate()}>
          {start.isPending ? <CircleNotch size={14} className="animate-spin mr-1.5" /> : null} Start my ~10-minute interview
        </Button>
      ) : (
        <>
          <div ref={scrollRef} className="space-y-2 max-h-64 overflow-y-auto mb-3 pr-1">
            {(session.messages || []).map((m, i) => (
              <div key={i} className={`max-w-[88%] ${m.role === "client" ? "ml-auto" : ""}`}>
                <p className={`text-xs rounded-md px-3 py-2 ${m.role === "client" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-800"}`}>{m.content}</p>
              </div>
            ))}
            {pendingMsg && (
              <div className="max-w-[88%] ml-auto"><p className="text-xs rounded-md px-3 py-2 bg-zinc-900 text-white">{pendingMsg}</p></div>
            )}
            {reply.isPending && <p className="text-[11px] text-zinc-400 flex items-center gap-1"><CircleNotch size={12} className="animate-spin" /> Thinking…</p>}
          </div>
          {done ? (
            session.summary && (
              <div className="rounded-md border border-emerald-200 bg-emerald-50/50 p-3" data-testid="intake-summary">
                <p className="text-[10px] uppercase tracking-wide text-emerald-700 mb-1">Interview recap</p>
                <p className="text-xs text-zinc-700">{session.summary}</p>
              </div>
            )
          ) : (
            <div className="flex gap-2">
              <Input data-testid="intake-answer-input" placeholder="Type your answer…" value={answer} onChange={(e) => setAnswer(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !reply.isPending && send()} className="h-8 text-xs" disabled={reply.isPending} />
              <Button data-testid="intake-send-btn" size="sm" className="h-8" disabled={!answer.trim() || reply.isPending} onClick={send}><PaperPlaneTilt size={13} /></Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
